import { IonButton } from '@ionic/react';
import type { Member } from '../../domain/household';
import type { Recipe } from '../../domain/recipe';
import { productList, recipeName } from '../i18n/format';
import { useMessages } from '../i18n/LanguageProvider';
import { FoodPlate } from '../pictures/FoodPlate';
import { ingredientPictures, recipePicture } from '../pictures/pictures';
import { addOnText } from '../recipes/add-on-text';
import { recipeById } from '../recipes/recipe-lookup';
import type { DinnerToday } from './today-summary';

// DE: Großer Teller mit dem Gericht und bis zu zwei kleinen Tellern mit Zutaten am Rand.
// EN: Large plate with the dish and up to two small ingredient plates on its edge.
function DishPlates({ recipe }: { recipe: Recipe }) {
  const [first, second] = ingredientPictures(recipe);
  return (
    <span className="app-dish-plates">
      <FoodPlate picture={recipePicture(recipe.id)} size={104} dish />
      {first && (
        <span className="app-dish-side app-dish-side-top">
          <FoodPlate picture={first} size={36} />
        </span>
      )}
      {second && (
        <span className="app-dish-side app-dish-side-bottom">
          <FoodPlate picture={second} size={36} />
        </span>
      )}
    </span>
  );
}

/**
 * DE: Zweite und dritte Frage: Was koche ich heute und wie lange dauert es?
 * EN: Second and third question: what do I cook today and how long does it take?
 */
export function DinnerCard({
  dinner,
  members,
  onRecipe,
  onNoEnergy,
}: {
  dinner: DinnerToday;
  members: readonly Member[];
  onRecipe: () => void;
  onNoEnergy: () => void;
}) {
  const { language, t } = useMessages();
  const recipe = recipeById(dinner.recipeId);
  return (
    <section className="app-card">
      <h2>{t.dinnerTitle}</h2>
      <div className="app-dish">
        <DishPlates recipe={recipe} />
        <div className="app-dish-text">
          <p className="app-dish-name">{recipeName(recipe, language)}</p>
          <p className="app-time">
            <span className="app-time-total">{t.minutes(dinner.totalMinutes)}</span>
            <span className="app-muted">{t.activeMinutes(dinner.activeMinutes)}</span>
          </p>
        </div>
      </div>
      <ul className="app-chips">
        <li className="app-chip">{t.portions(dinner.servings)}</li>
        {dinner.fish && <li className="app-chip app-chip-fish">{t.fish}</li>}
        {dinner.addOns.map((addOn) => (
          <li key={`${addOn.memberId}-${addOn.productId}`} className="app-chip app-chip-partner">
            {addOnText(addOn, members, language)}
          </li>
        ))}
        {dinner.allAtHome && <li className="app-chip">{t.allAtHome}</li>}
      </ul>
      {dinner.missing.length > 0 && (
        <p className="app-hint">{t.checkAtHome(productList(dinner.missing, language))}</p>
      )}
      <div className="app-actions app-actions-split">
        <IonButton onClick={onRecipe}>{t.recipe}</IonButton>
        <IonButton fill="outline" onClick={onNoEnergy}>
          {t.noEnergy}
        </IonButton>
      </div>
    </section>
  );
}
